/**
 * @file ScrollMore.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-27
 * @modified 2026-09-19
 * @description "There is more this way" — as a control rather than a smudge.
 *
 *              A gradient fade is the usual answer and looks like exactly what
 *              it is: a wash of background laid over whatever falls under it,
 *              which on a table of figures means half-erased numbers. A chevron
 *              says the same thing without touching the content, and can be
 *              clicked — the affordance and the action in one place.
 *
 *              It must NOT live inside the box that scrolls. An absolutely
 *              positioned child of a scrolling element travels with the content,
 *              so the cue slid up out of view exactly when it was needed. It
 *              belongs in a non-scrolling wrapper around that box, which is what
 *              .scroll-more-wrap is for.
 *
 *              Opacity is the proportion still unscrolled: full at the start,
 *              gone at the end. A cue that switches off at a threshold tells you
 *              nothing on the way; one that thins out tells you how much further
 *              there is. Once faded it leaves the tab order and stops taking
 *              clicks, so an invisible control cannot be reached.
 */

import React from 'react';
import './ScrollMore.css';

const ICONS = {
  down: 'keyboard_arrow_down',
  up: 'keyboard_arrow_up',
  right: 'chevron_right'
};
const LABELS = {
  down: 'Scroll down for more',
  up: 'Scroll up for more',
  right: 'Scroll right for more columns'
};

function ScrollMore({ scrollRef, remaining = 0, direction = 'down', label }) {
  const spent = remaining <= 0.001;
  const text = label || LABELS[direction];

  return (
    <button
      type="button"
      className={`scroll-more is-${direction}`}
      style={{ opacity: remaining, pointerEvents: spent ? 'none' : 'auto' }}
      aria-hidden={spent}
      tabIndex={spent ? -1 : 0}
      title={text}
      aria-label={text}
      onClick={() => {
        const el = scrollRef.current;
        if (!el) return;
        if (direction === 'right') {
          el.scrollBy({ left: Math.max(200, el.clientWidth * 0.7), behavior: 'smooth' });
        } else {
          // 'up' travels the same distance the other way. `remaining` is the
          // proportion still unscrolled in the cue's own direction, so an up
          // cue is handed 1 - remaining and fades out as it reaches the top,
          // exactly as the down cue fades out at the bottom.
          const step = el.clientHeight * 0.7;
          el.scrollBy({ top: direction === 'up' ? -step : step, behavior: 'smooth' });
        }
      }}
    >
      <span className="material-symbols-rounded">{ICONS[direction]}</span>
    </button>
  );
}

export default ScrollMore;
